import { cn } from '@/lib/utils';
import { clientInitials, type ClientTag } from './initials';

export interface ClientAvatarProps {
  name: string;
  photoUrl?: string | null;
  /**
   * Drives the tinted background on the initials fallback. Mirrors
   * the mobile `ClientAvatar`: Commercial → steel-blue tile, anything
   * else → neutral surface tile. Tints are theme-independent so a
   * "this is a business client" badge reads consistently regardless
   * of the active palette.
   */
  tag?: ClientTag;
  /**
   * Square edge length in CSS pixels. Defaults to 40 — the size used
   * by list rows. Other call sites pass 28 (chips) or 56 (detail
   * heroes).
   */
  size?: number;
  /** Corner radius in CSS pixels. Defaults to a 1/3 rounded-square. */
  radius?: number;
  /**
   * Optional override for the initials text size. Defaults to
   * `Math.round(size * 0.4)`, which matches the mobile rhythm.
   */
  fontSize?: number;
  className?: string;
}

/**
 * Single source of truth for client avatars on the web.
 *
 * Mirrors `apps/mobile/features/tally/components/ClientAvatar.tsx`
 * so the same client reads identically across platforms — uploading
 * a photo on mobile updates the web tile and vice versa, and the
 * "Commercial vs Residential" tint stays consistent.
 *
 * If a photo URL is set we render that; otherwise we paint a tinted
 * tile with up to two glyphs of initials. Photos load lazily so a
 * dense list (40+ rows) doesn't fire that many image requests
 * up-front.
 */
export function ClientAvatar({
  name,
  photoUrl,
  tag,
  size = 40,
  radius,
  fontSize,
  className,
}: ClientAvatarProps) {
  const r = radius ?? Math.round(size / 3);
  const text = fontSize ?? Math.round(size * 0.4);
  const isCommercial = tag === 'Commercial';
  const tileBg = isCommercial ? '#E1E8F2' : 'var(--surface-alt)';
  const tileFg = isCommercial ? '#3D4D62' : 'var(--ink)';
  const initials = clientInitials(name);

  return (
    <div
      className={cn(
        'relative inline-flex shrink-0 items-center justify-center overflow-hidden',
        className,
      )}
      style={{
        width: `${size}px`,
        height: `${size}px`,
        borderRadius: `${r}px`,
        background: tileBg,
      }}
      aria-label={`${name} avatar`}
    >
      {photoUrl ? (
        <img
          src={photoUrl}
          alt={`${name} photo`}
          loading="lazy"
          decoding="async"
          className="size-full object-cover"
          draggable={false}
        />
      ) : (
        <span
          aria-hidden
          style={{
            color: tileFg,
            fontSize: `${text}px`,
            letterSpacing: `${-text * 0.03}px`,
            fontWeight: 500,
            lineHeight: 1,
          }}
        >
          {initials || '·'}
        </span>
      )}
    </div>
  );
}
